import React from "react";
import { Star, X, Phone, MapPin, GraduationCap, Calendar, User } from "lucide-react";
import { STEPS } from "../data/questions";

export default function StudentProfileDrawer({ item, onClose }) {
  if (!item) return null;
  const ans = item.answers || {};
  const rating = parseInt(ans.q40, 10) || 0;
  const initials = (item.parent_name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "??";

  const submitted = item.created_at ? new Date(item.created_at).toLocaleString() : "—";

  // Group answers by section title from STEPS metadata (skip basics step)
  const sections = STEPS.filter((s) => s.questions).map((s) => ({
    title: s.title,
    subtitle: s.subtitle,
    items: s.questions.map((q) => ({
      n: q.n,
      key: q.id,
      label: q.q,
      type: q.type,
      value: ans[q.id],
    })),
  }));

  const recommendation = ans.q29;
  const recommendBadge = recommendation === "Definitely yes" || recommendation === "Yes";
  const testimonialConsent = ans.q39;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="student-profile-drawer">
      <div className="flex-1 bg-stone-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-3xl bg-sand-50 shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="relative bg-white border-b border-stone-200">
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-saffron-400 via-saffron-500 to-saffron-600" />
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center transition-colors"
            data-testid="student-profile-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-7 sm:px-10 pt-10 pb-8">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-saffron-400 to-saffron-600 text-white font-serif text-2xl sm:text-3xl flex items-center justify-center shadow-md shadow-saffron-500/30">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-saffron-600 font-medium">
                  Student Profile · Feedback Response
                </div>
                <h2 className="font-serif text-3xl text-stone-900 mt-1 leading-tight truncate">
                  {item.parent_name}
                </h2>
                <div className="text-sm text-stone-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> Student: <b className="text-stone-900 font-medium">{item.student_name}</b>
                  </span>
                </div>

                {/* Rating + badges */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {rating > 0 && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-saffron-100 border border-saffron-200">
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${i < rating ? "fill-saffron-500 text-saffron-500" : "text-saffron-200"}`}
                            strokeWidth={1.5}
                          />
                        ))}
                      </div>
                      <span className="text-xs font-medium text-saffron-700">{rating}.0 / 5</span>
                    </div>
                  )}
                  {recommendation && (
                    <span
                      className={`inline-flex items-center text-xs px-3 py-1.5 rounded-full font-medium border ${
                        recommendBadge
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-stone-100 text-stone-700 border-stone-200"
                      }`}
                    >
                      {recommendBadge ? "Would Recommend" : `Recommends: ${recommendation}`}
                    </span>
                  )}
                  {testimonialConsent === "Yes" && (
                    <span className="inline-flex items-center text-xs px-3 py-1.5 rounded-full font-medium bg-saffron-500 text-white">
                      Testimonial Consented
                    </span>
                  )}
                  {testimonialConsent === "Only with prior approval" && (
                    <span className="inline-flex items-center text-xs px-3 py-1.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      Consent w/ Approval
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Meta grid */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MetaCard icon={Calendar} label="Submitted" value={submitted} />
              <MetaCard icon={GraduationCap} label="Batch" value={item.batch_year} />
              <MetaCard icon={MapPin} label="City / Branch" value={item.city_branch} />
              <MetaCard icon={Phone} label="Contact" value={item.contact_number} mono />
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="px-7 sm:px-10 py-8 space-y-8" data-testid="student-profile-sections">
          {sections.map((sec) => {
            const answered = sec.items.filter((q) => {
              const v = q.value;
              return v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0) && String(v).trim() !== "";
            });
            if (answered.length === 0) return null;
            return (
              <section key={sec.title} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 bg-stone-50 border-b border-stone-100">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-saffron-600 font-medium">{sec.subtitle}</div>
                  <div className="font-serif text-lg text-stone-900 mt-0.5">{sec.title}</div>
                </div>
                <div className="divide-y divide-stone-100">
                  {answered.map((q) => (
                    <AnswerRow key={q.key} q={q} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value, mono }) {
  return (
    <div className="bg-stone-50 border border-stone-100 rounded-xl px-3.5 py-3">
      <div className="flex items-center gap-1.5 text-stone-500">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
        <span className="text-[10px] uppercase tracking-[0.18em] font-medium">{label}</span>
      </div>
      <div className={`mt-1 text-sm text-stone-900 ${mono ? "font-mono" : ""} truncate`}>{value || "—"}</div>
    </div>
  );
}

function AnswerRow({ q }) {
  const v = q.value;
  const isLong = q.type === "textarea";
  const isList = Array.isArray(v);
  const isStars = q.type === "stars";

  return (
    <div className="px-6 py-4 flex flex-col sm:flex-row gap-3 sm:gap-6">
      <div className="sm:w-2/5 shrink-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-medium">
          Q{q.n}
        </div>
        <div className="text-sm text-stone-700 mt-0.5 leading-snug">{q.label}</div>
      </div>
      <div className="flex-1 min-w-0">
        {isStars ? (
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${i < Number(v || 0) ? "fill-saffron-500 text-saffron-500" : "text-stone-200"}`}
                strokeWidth={1.5}
              />
            ))}
            <span className="text-sm text-stone-700 ml-2 font-medium">{v} / 5</span>
          </div>
        ) : isList ? (
          <div className="flex flex-wrap gap-1.5">
            {v.map((x) => (
              <span key={x} className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-saffron-50 text-saffron-700 border border-saffron-100">
                {x}
              </span>
            ))}
          </div>
        ) : isLong ? (
          <p className="text-sm text-stone-800 whitespace-pre-wrap font-serif italic leading-relaxed bg-stone-50 border-l-2 border-saffron-400 pl-3 py-1.5 rounded-sm">
            “{String(v)}”
          </p>
        ) : (
          <span className="inline-flex items-center text-sm px-3 py-1.5 rounded-lg bg-saffron-50 text-saffron-700 border border-saffron-100 font-medium">
            {String(v)}
          </span>
        )}
      </div>
    </div>
  );
}
